/**
 * IncidentReportPanel component
 *
 * Sheet-based side panel for incident reports. Displays a formatted,
 * export-ready single-page report that consolidates issue data, AI
 * narrative, traces, and tickets into a clean document layout suitable
 * for sharing with stakeholders or compliance documentation.
 */

import { useCallback, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Download,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Ticket,
} from "lucide-react";
import { toast } from "sonner";
import {
  SEVERITY_BG,
  SEVERITY_BADGE,
  SEVERITY_BORDER_LEFT,
  SEVERITY_DOT,
} from "@/constants/severity";

interface AiNarrative {
  executive_summary?: string | null;
  timeline_narrative?: string | null;
  risk_assessment?: string | null;
  recommended_actions?: string[] | null;
  root_cause_hypothesis?: string | null;
}

interface ReportSummary {
  total_traces: number;
  total_comments: number;
  total_activities: number;
  total_tickets: number;
  traces_truncated?: boolean;
  traces_limit?: number;
}

interface ReportIssue {
  id?: number;
  title?: string;
  severity?: string;
  status?: string;
  issue_type?: string;
  description?: string;
  detected_at?: string;
  risk_score?: number;
}

interface ReportTrace {
  id?: number;
  source?: string;
  event_type?: string;
  severity?: string;
  anomaly_score?: number;
  timestamp?: string;
}

interface ReportTicket {
  id?: number;
  provider?: string;
  external_ticket_id?: string;
  external_ticket_url?: string;
  project_key?: string;
  summary?: string;
  external_status?: string;
  created_at?: string;
}

export interface IncidentReport {
  issue?: ReportIssue;
  traces?: ReportTrace[];
  comments?: Array<Record<string, unknown>>;
  activities?: Array<Record<string, unknown>>;
  tickets?: ReportTicket[];
  summary?: ReportSummary;
  ai_narrative?: AiNarrative | null;
  fallback_reason?: string | null;
}

interface IncidentReportPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: number;
  report: IncidentReport | null;
  isLoading?: boolean;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

function SeverityDot({ severity }: { severity?: string }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${
        SEVERITY_DOT[severity ?? "info"] ?? "bg-gray-400"
      }`}
    />
  );
}

/** Build an inline evidence summary string like "Based on 4 traces, 2 comments, ..." */
function buildEvidenceSummary(summary: ReportSummary): string {
  const parts: string[] = [];
  if (summary.total_traces > 0)
    parts.push(`${summary.total_traces} trace${summary.total_traces !== 1 ? "s" : ""}`);
  if (summary.total_comments > 0)
    parts.push(`${summary.total_comments} comment${summary.total_comments !== 1 ? "s" : ""}`);
  if (summary.total_activities > 0)
    parts.push(
      `${summary.total_activities} activit${summary.total_activities !== 1 ? "ies" : "y"}`,
    );
  if (summary.total_tickets > 0)
    parts.push(`${summary.total_tickets} ticket${summary.total_tickets !== 1 ? "s" : ""}`);

  if (parts.length === 0) return "No evidence collected.";
  if (parts.length === 1) return `Based on ${parts[0]}.`;
  if (parts.length === 2) return `Based on ${parts[0]} and ${parts[1]}.`;
  return `Based on ${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}.`;
}

export function IncidentReportPanel({
  open,
  onOpenChange,
  issueId,
  report,
  isLoading,
  onRegenerate,
  isRegenerating,
}: IncidentReportPanelProps) {
  const [copied, setCopied] = useState(false);

  const handleDownload = useCallback(() => {
    if (!report) return;
    const json = JSON.stringify(report, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `incident-report-issue-${issueId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [report, issueId]);

  const handleCopy = useCallback(() => {
    if (!report) return;
    const text = report.ai_narrative?.executive_summary
      ? `${report.ai_narrative.executive_summary}\n\n${report.ai_narrative.risk_assessment ?? ""}`
      : JSON.stringify(report, null, 2);
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        toast.error("Failed to copy to clipboard");
      },
    );
  }, [report]);

  const narrative = report?.ai_narrative;
  const summary = report?.summary;
  const generatedAt = new Date().toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-xl w-full overflow-y-auto"
      >
        <SheetHeader className="pb-2">
          <SheetTitle>Incident Report</SheetTitle>
          <SheetDescription>
            {report?.issue?.title
              ? `Report for: ${report.issue.title}`
              : `Issue #${issueId}`}
          </SheetDescription>
          {report && (
            <p className="text-xs text-muted-foreground">
              Generated on {generatedAt}
            </p>
          )}
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Generating incident report...
            </span>
          </div>
        ) : !report ? (
          <div className="py-16 text-center">
            <p className="text-sm text-muted-foreground">
              No report data available.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Action Bar */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleDownload}
              >
                <Download className="h-3.5 w-3.5" />
                Download JSON
              </Button>
              {onRegenerate && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={onRegenerate}
                  disabled={isRegenerating}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${isRegenerating ? "animate-spin" : ""}`}
                  />
                  {isRegenerating ? "Regenerating..." : "Regenerate"}
                </Button>
              )}
            </div>

            {/* Fallback Warning */}
            {report.fallback_reason && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  AI summary unavailable: {report.fallback_reason}. Showing raw
                  data instead.
                </span>
              </div>
            )}

            {/* Issue Summary Card */}
            {report.issue && (
              <div
                className={`rounded-lg border border-l-4 p-4 space-y-3 ${
                  SEVERITY_BORDER_LEFT[report.issue.severity ?? "info"] ??
                  "border-l-gray-400"
                } ${SEVERITY_BG[report.issue.severity ?? "info"] ?? ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-semibold leading-snug flex-1">
                    {report.issue.title}
                  </h3>
                  {report.issue.severity && (
                    <Badge
                      className={`text-[11px] font-semibold uppercase px-2 py-0.5 border ${
                        SEVERITY_BADGE[report.issue.severity] ?? ""
                      }`}
                    >
                      {report.issue.severity}
                    </Badge>
                  )}
                </div>

                {/* Metadata as dl grid */}
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                  {report.issue.status && (
                    <div>
                      <dt className="text-[11px] text-muted-foreground">
                        Status
                      </dt>
                      <dd className="text-xs mt-0.5">{report.issue.status}</dd>
                    </div>
                  )}
                  {report.issue.issue_type && (
                    <div>
                      <dt className="text-[11px] text-muted-foreground">
                        Type
                      </dt>
                      <dd className="text-xs mt-0.5">
                        {report.issue.issue_type}
                      </dd>
                    </div>
                  )}
                  {report.issue.detected_at && (
                    <div>
                      <dt className="text-[11px] text-muted-foreground">
                        Detected
                      </dt>
                      <dd className="text-xs mt-0.5">
                        {new Date(report.issue.detected_at).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                </dl>

                {/* Risk score bar */}
                {report.issue.risk_score != null && (() => {
                  const pct = report.issue.risk_score <= 1
                    ? report.issue.risk_score * 100
                    : report.issue.risk_score;
                  return (
                    <div>
                      <dt className="text-[11px] text-muted-foreground">
                        Risk Score
                      </dt>
                      <dd className="flex items-center gap-2 mt-1">
                        <div className="h-1 flex-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              pct >= 75
                                ? "bg-red-500"
                                : pct >= 50
                                  ? "bg-orange-500"
                                  : "bg-blue-400"
                            }`}
                            style={{
                              width: `${Math.min(pct, 100)}%`,
                            }}
                          />
                        </div>
                        <span
                          className={`text-xs font-medium tabular-nums ${
                            pct >= 75
                              ? "text-red-600 dark:text-red-400"
                              : pct >= 50
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-muted-foreground"
                          }`}
                        >
                          {Math.round(pct)}/100
                        </span>
                      </dd>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Evidence Summary (inline text) */}
            {summary && (
              <p className="text-sm text-muted-foreground">
                {buildEvidenceSummary(summary)}
                {summary.traces_truncated && (
                  <span className="italic">
                    {" "}
                    (Showing first {summary.traces_limit} traces.)
                  </span>
                )}
              </p>
            )}

            <hr className="border-border" />

            {/* Analysis Section */}
            {narrative && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Analysis
                    </h4>
                    <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
                      Auto-generated
                    </span>
                  </div>

                  {narrative.executive_summary && (
                    <div className="border-l-2 border-muted-foreground/20 pl-4">
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        Executive Summary
                      </h4>
                      <p className="text-sm leading-relaxed">
                        {narrative.executive_summary}
                      </p>
                    </div>
                  )}

                  {narrative.risk_assessment && (
                    <div className="border-l-2 border-orange-400/40 pl-4">
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        Risk Assessment
                      </h4>
                      <p className="text-sm leading-relaxed">
                        {narrative.risk_assessment}
                      </p>
                    </div>
                  )}

                  {narrative.root_cause_hypothesis && (
                    <div className="border-l-2 border-muted-foreground/20 pl-4">
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        Root Cause
                      </h4>
                      <p className="text-sm leading-relaxed">
                        {narrative.root_cause_hypothesis}
                      </p>
                    </div>
                  )}

                  {narrative.timeline_narrative && (
                    <div className="border-l-2 border-muted-foreground/20 pl-4">
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        Timeline
                      </h4>
                      <p className="text-sm leading-relaxed whitespace-pre-line">
                        {narrative.timeline_narrative}
                      </p>
                    </div>
                  )}

                  {narrative.recommended_actions &&
                    narrative.recommended_actions.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground mb-2">
                          Recommended Actions
                        </h4>
                        <ol className="space-y-1.5">
                          {narrative.recommended_actions.map((action, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3 text-sm"
                            >
                              <span className="flex-shrink-0 text-xs font-medium text-muted-foreground w-5 pt-0.5 text-right tabular-nums">
                                {idx + 1}.
                              </span>
                              <span className="leading-relaxed">{action}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                </div>
                <hr className="border-border" />
              </>
            )}

            {/* Traces Section */}
            {report.traces && report.traces.length > 0 && (
              <>
                <div className="space-y-2">
                  <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Traces ({report.traces.length})
                  </h4>
                  <div className="rounded-lg border bg-card divide-y divide-border">
                    {report.traces.map((trace, idx) => (
                      <div
                        key={trace.id ?? idx}
                        className="flex items-center gap-2.5 px-3 py-2 text-sm"
                      >
                        <SeverityDot severity={trace.severity} />
                        <span className="font-mono text-xs text-muted-foreground flex-shrink-0">
                          #{trace.id ?? idx + 1}
                        </span>
                        {trace.event_type && (
                          <span className="truncate text-xs">
                            {trace.event_type}
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                          {trace.anomaly_score != null &&
                            trace.anomaly_score > 0 && (
                              <span
                                className={`text-xs font-medium tabular-nums ${
                                  trace.anomaly_score >= 0.75
                                    ? "text-red-600 dark:text-red-400"
                                    : trace.anomaly_score >= 0.5
                                      ? "text-orange-600 dark:text-orange-400"
                                      : "text-muted-foreground"
                                }`}
                              >
                                {trace.anomaly_score.toFixed(2)}
                              </span>
                            )}
                          {trace.timestamp && (
                            <span className="text-[11px] text-muted-foreground">
                              {new Date(trace.timestamp).toLocaleString(
                                undefined,
                                {
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <hr className="border-border" />
              </>
            )}

            {/* Tickets Section */}
            {report.tickets && report.tickets.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Linked Tickets ({report.tickets.length})
                </h4>
                <div className="rounded-lg border bg-card divide-y divide-border">
                  {report.tickets.map((ticket, idx) => (
                    <div
                      key={ticket.id ?? idx}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm"
                    >
                      <Ticket className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="font-mono text-xs font-medium truncate">
                        {ticket.external_ticket_id ?? `Ticket #${ticket.id}`}
                      </span>
                      {ticket.external_status && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] uppercase px-1.5 py-0 flex-shrink-0"
                        >
                          {ticket.external_status}
                        </Badge>
                      )}
                      {ticket.provider && (
                        <span className="text-[11px] text-muted-foreground capitalize flex-shrink-0">
                          {ticket.provider}
                        </span>
                      )}
                      {ticket.external_ticket_url && (
                        <a
                          href={ticket.external_ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                          title={`Open in ${ticket.provider ?? "provider"}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
